import { FormEvent, FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { LoadingOverlay } from "@/components";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import {
	useApproveMcpAuthorizeMutation,
	useDenyMcpAuthorizeMutation,
	useGetMcpAuthorizeDetailsQuery,
} from "@/pages/Private/redux/mcp/mcp.api";
import { useAppSelector } from "@/redux/hooks";
import { ROUTE_CONFIG } from "@/routes/config";
import { usePageTitle } from "@/utils/usePageTitle";

import FormContainer from "../../components/FormContainer";
import { clearMcpAuthorize, rememberMcpAuthorize } from "../../mcpReturn";

/**
 * The consent page of the MCP server's OAuth flow — BCM360-383. An AI client (Claude, ChatGPT…)
 * asks to work in the scenario editor as the logged-in user; only a Global Admin can allow it.
 * Allowing or refusing sends the browser back to the client.
 */
export const McpAuthorize: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, string>) => t(`mcpAuthorize.${key}`, options);
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const { user, token, otpToken, pendingEmailOtpUserId } = useAppSelector(authSelector);
	const loggedIn = !!user && !!token && !otpToken && !pendingEmailOtpUserId;
	const params = useMemo(() => Object.fromEntries(new URLSearchParams(search)), [search]);
	const { data, error, isLoading } = useGetMcpAuthorizeDetailsQuery(params, { skip: !loggedIn });
	const [approve, { isLoading: approving }] = useApproveMcpAuthorizeMutation();
	const [deny, { isLoading: denying }] = useDenyMcpAuthorizeMutation();
	const [leaving, setLeaving] = useState(false);

	usePageTitle(ts("title"));

	useEffect(() => {
		if (!loggedIn) {
			rememberMcpAuthorize(`${pathname}${search}`);
			navigate(ROUTE_CONFIG.LOGIN);
		} else {
			clearMcpAuthorize();
		}
	}, [loggedIn, pathname, search, navigate]);

	const leave = (redirectTo: string) => {
		setLeaving(true);
		window.location.assign(redirectTo);
	};

	const onAllow = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		leave((await approve(params).unwrap()).redirectTo);
	};

	const onDeny = async () => leave((await deny(params).unwrap()).redirectTo);

	if (!loggedIn || isLoading || leaving) {
		return <LoadingOverlay />;
	}

	const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "";
	const refused = (error as { data?: { error_description?: string } } | undefined)?.data
		?.error_description;

	return (
		<div className="flex h-full w-full items-center justify-center" data-testid="mcp-authorize">
			{refused || !data ? (
				<FormContainer
					hideActions
					description={refused ?? ts("invalid")}
					handleSubmit={(event) => event.preventDefault()}
					isLoading={false}
					submitButtonText=""
					title={ts("title")}
				>
					<span />
				</FormContainer>
			) : data.allowed ? (
				<FormContainer
					description={ts("description", { client: data.clientName, user: name })}
					handleSubmit={onAllow}
					isLoading={approving || denying}
					secondaryButtonAction={onDeny}
					secondaryButtonText={ts("deny")}
					submitButtonText={ts("allow")}
					title={ts("title")}
				>
					<ul className="mb-4 space-y-1 text-[14px] leading-[20px] text-primary-gray">
						{[ts("canRead"), ts("canEdit"), ts("canPaid")].map((line) => (
							<li key={line} className="flex gap-2">
								<span aria-hidden="true">•</span>
								<span>{line}</span>
							</li>
						))}
					</ul>
					<p className="text-[13px] leading-[18px] text-primary-gray-lighter">
						{ts("returnsTo", { host: data.redirectHost })} {ts("revoke")}
					</p>
				</FormContainer>
			) : (
				<FormContainer
					description={ts("notAllowed")}
					handleSubmit={(event) => {
						event.preventDefault();
						onDeny();
					}}
					isLoading={denying}
					submitButtonText={ts("back")}
					title={ts("title")}
				>
					<span />
				</FormContainer>
			)}
		</div>
	);
};
